import { PERSIST_SCHEMA_VERSION } from "@funded-here-interface/common/src/constant/persistConfig";
import { configureStore } from "@reduxjs/toolkit";
import { combineReducers } from "redux";
import { persistStore, persistReducer, type PersistedState } from "redux-persist";
import storageSession from "redux-persist/lib/storage/session";

import { adminReducers } from "./reducers";

// One-time migration: clear orphaned localStorage persist key
// (admin switched from localStorage to sessionStorage for persistence)
if (localStorage.getItem("persist:root")) {
  localStorage.removeItem("persist:root");
}

/**
 * Admin persist migration. Exported (rather than inlined in the config below) so it
 * can be unit-tested — this module calls persistStore at import time, so the config
 * object itself is awkward to reach from a test.
 *
 * It forwards `auth` and drops everything else, which is the right default for a
 * hop to an unknown shape: the user stays logged in, and any slice whose shape we
 * can't vouch for is rebuilt from its reducer's initialState.
 *
 * Deliberately NOT default-filling `auth` from the slice's initial state. That
 * would be the fix if a newly-added key's absence were observable — autoMergeLevel1
 * replaces the whole slice rather than deep-merging it, so a bump alone leaves a new
 * key `undefined` — but see the worked example in persistConfig.ts: for `auth.roles`
 * the only reader is an `===` against a role name, which `undefined` and the initial
 * `""` both fail identically. Filling it would have changed no behaviour while
 * costing every mid-onboarding investor their `biz`/`prof` state. Add the fill when a
 * key arrives whose default is actually distinguishable at its read site.
 *
 * `_persist` is returned for shape only: persistReducer rebuilds it from
 * config.version at REHYDRATE and discards whatever this returns. Don't put logic there.
 */
export const adminPersistMigrate = async (
  state: PersistedState,
  currentVersion: number,
): Promise<PersistedState> => {
  if (!state) return undefined;
  if (state._persist.version === currentVersion) return state;
  const { auth } = state as PersistedState & { auth?: unknown };
  return { auth, _persist: state._persist } as PersistedState;
};

const persistConfig = {
  key: "root",
  version: PERSIST_SCHEMA_VERSION,
  storage: storageSession,
  whitelist: ["auth"], // Add other reducers you want to persist here
  migrate: adminPersistMigrate,
};
const persistedReducer = persistReducer(persistConfig, combineReducers(adminReducers));

const store = configureStore({
  reducer: persistedReducer,
});

const persistor = persistStore(store);

export { store, persistor };
export type RootState = ReturnType<typeof store.getState>;
